import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import {
  color,
  compose,
  flexbox,
  layout,
  space,
  system,
  typography,
} from 'styled-system';

import { defaultExcludedProps, forwardValidProp } from '../../utils/style';

const baseStyles = ({ theme, lineBreak, whiteSpace }) => css`
  color: ${theme.colors.text.foreground};
  font-style: normal;
  font-weight: ${theme.typography.fontWeights.regular};
  line-break: ${lineBreak};
  white-space: ${whiteSpace};
`;

const sizesStyles = ({ theme, size }) => {
  const sizesStylesMap = {
    size1: css`
      font-size: ${theme.typography.textsMobile.size1.fontSize};
      line-height: ${theme.typography.textsMobile.size1.lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.texts.size1.fontSize};
        line-height: ${theme.typography.texts.size1.lineHeight};
      }
    `,
    size2: css`
      font-size: ${theme.typography.textsMobile.size2.fontSize};
      line-height: ${theme.typography.textsMobile.size2.lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.texts.size2.fontSize};
        line-height: ${theme.typography.texts.size2.lineHeight};
      }
    `,
    size3: css`
      font-size: ${theme.typography.textsMobile.size3.fontSize};
      line-height: ${theme.typography.textsMobile.size3.lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.texts.size3.fontSize};
        line-height: ${theme.typography.texts.size3.lineHeight};
      }
    `,
  };

  return size ? sizesStylesMap[size] : sizesStylesMap.size1;
};

const boldStyles = ({ theme, bold }) =>
  bold &&
  css`
    font-weight: ${theme.typography.fontWeights.bold};
  `;

const italicStyles = ({ italic }) =>
  italic &&
  css`
    font-style: italic;
  `;

const upperStyles = ({ upper }) =>
  upper &&
  css`
    text-transform: uppercase;
  `;

const StyledText = styled('p', {
  shouldForwardProp: (prop) =>
    forwardValidProp(prop, [
      ...defaultExcludedProps,
      'type',
      'fontSize',
      'width',
    ]),
})`
  ${system({
    wordBreak: {
      property: 'wordBreak',
    },
  })}
  ${baseStyles}
  ${sizesStyles}
  ${boldStyles}
  ${italicStyles}
  ${upperStyles}
  ${compose(color, flexbox, layout, space, typography)}
`;

const Text = forwardRef(({ ...props }, ref) => (
  <StyledText ref={ref} {...props} />
));

Text.HTML_MARKUP_OPTIONS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'article',
  'div',
  'em',
  'span',
  'a',
  'button',
  'label',
  'time',
  'figcaption',
  'li',
];
Text.SIZES = ['size1', 'size2', 'size3'];

Text.displayName = 'Text';

Text.defaultProps = {
  as: 'p',
  bold: false,
  children: null,
  italic: false,
  lineBreak: null,
  upper: false,
  size: 'size1',
  whiteSpace: 'pre-wrap',
};

Text.propTypes = {
  /**
   * HTML markup to be rendered
   */
  as: PropTypes.oneOf([
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'article',
    'div',
    'em',
    'span',
    'a',
    'button',
    'label',
    'time',
    'figcaption',
    'li',
  ]),
  /**
   * Renders a bold text
   */
  bold: PropTypes.bool,
  /**
   * Text or children to be rendered inside component
   */
  children: PropTypes.node,
  /**
   * Renders a italic text
   */
  italic: PropTypes.bool,
  /**
   * `line-break` css prop
   */
  lineBreak: PropTypes.string,
  /**
   * Renders a text in uppercase
   */
  upper: PropTypes.bool,
  /**
   * Renders a text with a determined size
   */
  size: PropTypes.oneOf(['size1', 'size2', 'size3']),
  /**
   * `white-space` css prop
   */
  whiteSpace: PropTypes.string,
  /**
   * `word-break` css prop
   */
  wordBreak: PropTypes.string,
  /**
   * Implements `styled-system` [color](https://styled-system.com/api/#color) utility
   */
  ...propTypes.color,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
  /**
   * Implements `styled-system` [typography](https://styled-system.com/api#typography) utility
   */
  ...propTypes.typography,
};

export default Text;
